import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,MenuItem ,
  Paper, IconButton,FormControl ,InputLabel ,Select,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Trophy, Award, Medal } from "lucide-react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../AuthProvider";
import { useNavigate } from "react-router-dom";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const StudentsTestResults = () => {
        const navigate = useNavigate();
  const { TestType } = useParams();
  const { oId,pId,board } = useAuth();
  const [students, setStudents] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

const [sscClassOptions, setSscClassOptions] = useState([]);
const [sscSectionOptions, setSscSectionOptions] = useState([]);
const [sscSelectedClass, setSscSelectedClass] = useState("");
const [sscSelectedSection, setSscSelectedSection] = useState("");
const [sscClassSectionMap, setSscClassSectionMap] = useState({});

  // ---- Fetch Class + Sections for SSC (School) ----
const fetchClassRegistrationData = async () => {
  try {
    const response = await fetch(`${baseUrl}/api/dynamic-classReg/class-registrations/${oId}/${pId}`);
    const data = await response.json();

    if (response.ok && Array.isArray(data)) {
      const classSectionMap = {};

      data.forEach(item => {
        const className = item.classRegistration;
        const sectionList = item.sections.split(",").map(s => s.trim());

        if (!classSectionMap[className]) {
          classSectionMap[className] = new Set();
        }

        sectionList.forEach(section => classSectionMap[className].add(section));
      });

      const classes = Object.keys(classSectionMap);
      setSscClassOptions(classes); // Populate class dropdown
      setSscClassSectionMap(classSectionMap); // Save mapping

      // ✅ Set default class and sections (first record)
      const defaultClass = classes[0];
      const defaultSections = Array.from(classSectionMap[defaultClass]);
      const defaultSection = defaultSections.includes("Section-A")
        ? "Section-A"
        : defaultSections[0] || "";

      setSscSelectedClass(defaultClass);
      setSscSectionOptions(defaultSections);
      setSscSelectedSection(defaultSection);
    }
  } catch (error) {
    console.error("Error fetching SSC data:", error);
  }
};

const fetchRomanClasses = async () => {
  try {
    setLoading(true);

    const res = await fetch(
      `${baseUrl}/api/online/exam/roman-classes?type=${TestType}`
    );

    if (!res.ok) throw new Error("Failed to fetch classes");

    const data = await res.json();

    console.log("API raw data:", data);

    let classesArray = [];

    if (Array.isArray(data) && data.length > 0) {
      // 🔥 Split comma string into array
      classesArray = data[0]
        .split(",")
        .map(cls => cls.trim())
        .filter(Boolean);
    }

    console.log("Formatted classes:", classesArray);

    setSscClassOptions(classesArray);
    setSscSelectedClass(classesArray[0] || "");
    
  } catch (error) {
    console.error("Error fetching roman classes:", error);
  } finally {
    setLoading(false);
  }
};
useEffect(() => {
  if (TestType) {
    fetchRomanClasses();
  }
}, [TestType]);

  useEffect(() => {
    const fetchTopStudents = async () => {
      if (!sscSelectedClass) return;
      try {
        setLoading(true);
        const response = await fetch(
          `${baseUrl}/api/submission/students/${pId}/${sscSelectedClass}/${TestType}`
        );
        if (!response.ok) throw new Error("Failed to fetch top students");
        const data = await response.json();
        setStudents(data || []);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTopStudents();
  }, [pId,sscSelectedClass, TestType]);

  // pick first 3 safely
  const first = students[0];
  const second = students[1];
  const third = students[2];

  const medals = [
    {
      place: "1st",
      color: "#FFEB99", // gold background
      medalBg: "#FFD700", // gold circle
      medalIcon: "🏆",
      iconColor: "#b58900",
      borderColor: "#FFCC00",
      data: first,
    },
    {
      place: "2nd",
      color: "#CBE3FF", // silver background
      medalBg: "#C0C0C0", // silver circle
      medalIcon: "🥈",
      iconColor: "#555",
      borderColor: "#B0B0B0",
      data: second,
    },
 
    {
    place: "3rd",
    color: "#EFD6FF", // platinum/purple background
    medalBg: "#D9B3FF", // light platinum circle
    medalIcon: "🥉", // bronze-style but matches color palette
    iconColor: "#6a1b9a",
    borderColor: "#BA68C8",
    data: third,
  },
  ];



const MedalCard = ({
  place,
  color,
  medalBg,
  medalIcon,
  iconColor,
  borderColor,
  data,
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{ position: "relative" }}
    >
      {/* Medal circle */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: medalBg,
          border: `4px solid ${borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 38,
          mb: -3,
          zIndex: 2,
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
        }}
      >
        {medalIcon}
      </Box>

      {/* Card */}
      <Card
        sx={{
          width: 260,
          borderRadius: "16px",
          background: color,
          boxShadow: "0 5px 12px rgba(0,0,0,0.15)",
          textAlign: "center",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
          },
        }}
      >
        <CardContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{
              background: "#fff",
              borderRadius: "12px",
              p: 2,
              mt: 1,
              boxShadow: "inset 0 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            <Row label="Student Name:" value={data?.name || "N/A"} />
            <Row label="Class:" value={data?.romanNumberClass || "N/A"} />
            {/* <Row label="Section:" value={data?.sections || ""} /> */}
            <Row label="Roll No:" value={data?.studentRollNumber || "N/A"} />
            <Row
              label="Marks:"
              value={`${data?.correctAnswers || 0}/${data?.questionsCount || 0}`}
              valueColor="#2e7d32"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

  // ✅ Reusable Row Component
const Row = ({ label, value, valueColor = "#2c3e50" }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    width="100%"
    sx={{ borderBottom: "1px solid #eee", pb: 0.5 }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600, color: "#555" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: valueColor, fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

  return (
    <Box
      sx={{
        background: "#eef1f5",
        margin:'5% 1% 0% 1%'
      }}
    >
      {/* 🧊 MAIN DASHBOARD CARD */}
      <Card
        sx={{
          borderRadius: "20px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          background: "#ffffff",
        //   padding: "40px",
        }}
      >
         <Box display="flex" alignItems="center" sx={{margin:'10px'}}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: "#2e2a54" }}>
                    <KeyboardBackspaceIcon fontSize="medium" />
                    </IconButton>
                    <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "#2e2a54" }}
                    >
                    Class Wise Students Performance
                    </Typography>
                </Box>
      
        {/* 🌟 TOP 3 CARDS code*/}
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      gap={4}
      flexWrap="wrap"
      sx={{ mt: 4 ,mb: 4}}
    >
      {medals.map((m) => (
        <MedalCard key={m.place} {...m} />
      ))}
    </Box>

        {/* 📊 RESULTS TABLE */}
        <Card
          sx={{
            borderRadius: "16px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
            background: "#fff",
            border: '1px solid #ccc',
            padding: "16px",
            margin:'10px'
          }}
        >
         <Box
  display="flex"
  alignItems="center"
  justifyContent="space-between"
  sx={{ mb: 2 }}
>
  {/* Empty left spacer to balance center alignment */}
  <Box sx={{ width: "120px" }} />

  {/* Center title */}
  <Typography
    variant="h6"
    sx={{
      fontWeight: "bold",
      color: "#37474f",
      textAlign: "center",
      flex: 1,
    }}
  >
    Class Wise Students Results
  </Typography>

  {/* Right side - class & section dropdowns */}
{board === "SSC" && (
  <Box display="flex" alignItems="center">
    <FormControl size="small" sx={{ minWidth: 180, mr: 2 }}>
      <InputLabel id="class-select-label">Select Class</InputLabel>

      <Select
        labelId="class-select-label"
        value={sscSelectedClass}
        onChange={(e) => setSscSelectedClass(e.target.value)}
        label="Select Class"
        sx={{ height: 35 }}
      >
        {/* 🔄 Loading State */}
        {loading ? (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              Loading...
            </Box>
          </MenuItem>
        ) : sscClassOptions.length === 0 ? (
          /* ❌ No Data */
          <MenuItem disabled>No Classes Found</MenuItem>
        ) : (
          /* ✅ API Data */
          sscClassOptions.map((cls, index) => (
            <MenuItem key={index} value={cls}>
              {cls}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  </Box>
)}
</Box>



          <Paper
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 3px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#fff9c4" }}>
                  <TableCell sx={{ fontWeight: "bold" ,borderRight: "1px solid #ddd",textAlign: "center",}}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: "bold" ,borderRight: "1px solid #ddd",textAlign: "center",}}>Class</TableCell>
                  <TableCell sx={{ fontWeight: "bold",borderRight: "1px solid #ddd",textAlign: "center", }}>Student Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" ,borderRight: "1px solid #ddd",textAlign: "center",}}>Roll No</TableCell>
                  <TableCell sx={{ fontWeight: "bold" ,borderRight: "1px solid #ddd",textAlign:'center'}}>
                    Correct Answers
                  </TableCell>
                </TableRow>
              </TableHead>
               { loading ?(
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    colSpan={5}
                                    style={{ textAlign: "center", padding: "20px" }}
                                  >
                                    <Box
                                      display="flex"
                                      flexDirection="column"
                                      alignItems="center"
                                    >
                                      <CircularProgress />
                                      <Typography variant="body2" sx={{ mt: 1 }}>
                                        Loading Data...
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                                
                              </TableBody>
                            ) : students.length === 0 ? (
                              <TableBody>
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ textAlign: "center",padding:'10px' }}>
                                    No Students data found
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            ) : (
              <TableBody>
                {students.map((s, i) => (
                  <TableRow
                    key={s.studentId}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f7f9fc",
                      },
                    }}
                  >
                    <TableCell sx={{borderRight: "1px solid #ddd",textAlign: "center",}}>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: "bold" ,borderRight: "1px solid #ddd",textAlign: "center",}}>{s.romanNumberClass}</TableCell>
                    <TableCell sx={{ fontWeight: "bold" ,borderRight: "1px solid #ddd",textAlign: "center",}}>{s.name}</TableCell>
                    <TableCell sx={{ fontWeight: "bold" ,borderRight: "1px solid #ddd",textAlign: "center",}}>{s.studentRollNumber}</TableCell>
                   <TableCell
  sx={{
    fontWeight: 600,
    borderRight: "1px solid #ddd",
    textAlign: "center",
    color:
      i === 0
        ? "#d32f2f" // 🔴 Red for 1st (stands out strong)
        : i === 1
        ? "#1976d2" // 🔵 Blue for 2nd
        : i === 2
        ? "#f57c00" // 🟠 Orange for 3rd
        : i % 2 === 0
        ? "#2e7d32" // 🟢 Green for even lower ranks
        : "#6a1b9a", // 🟣 Purple for odd lower ranks
    background:
      i === 0
        ? "rgba(244, 67, 54, 0.1)"
        : i === 1
        ? "rgba(33, 150, 243, 0.1)"
        : i === 2
        ? "rgba(255, 152, 0, 0.1)"
        : "rgba(0,0,0,0.03)",
    borderRadius: "6px",
  }}
>
  {s.correctAnswers || 0}/{s.questionsCount || 0}
</TableCell>

                  </TableRow>
                ))}
              </TableBody>
               )}
            </Table>
          </Paper>
        </Card>
      </Card>
    </Box>
  );
};

export default StudentsTestResults;